import { Flex, Modal as AntdModal, Button, Form, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useContext, useState } from 'react';
import AntdFormItem from '../../../../../../../../../components/AntdFormItem';
import AntdSelect from '../../../../../../../../../components/AntdSelect';
import AntdInput from '../../../../../../../../../components/AntdInput';
import Modal from '../../../../../../../../../components/Modal';
import AccionaButton from '../../../../../../../../../components/AccionaButton';
import {
  getTagsByProject,
  createTag,
  deleteTag,
} from '../../../../../../../../../services/api/tags';
import { NotificationsContext } from '../../../../../../../../../context/NotificationsContext';

const TagSelector = ({
  form,
  project,
  disabled,
  required,
  tags,
  fetchingTags,
  setTags,
}) => {
  const intl = useIntl();
  const { pushNotification } = useContext(NotificationsContext);
  const [tagForm] = Form.useForm();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleCreateTag = () => {
    tagForm.resetFields();
    setShowCreateModal(true);
  };

  const handleSubmitTag = (values) => {
    const tagName = values.tagName?.trim();
    if (tagName) {
      createTag({
        name: tagName,
        id_project: project.id_project,
      })
        .then((response) => {
          const newTagId = response.data.id;
          return getTagsByProject(project.id_project).then((tagsResponse) => {
            const updatedTags = tagsResponse.data.data;
            setTags(updatedTags);
            form.setFieldValue('tag', newTagId);
            setShowCreateModal(false);
          });
        })
        .catch((error) => {
          if (error.response?.status === 400) {
            pushNotification({
              type: 'error',
              message: intl.formatMessage({
                id: 'tag.name_in_use',
              }),
            });
          } else {
            pushNotification({
              type: 'error',
              message: intl.formatMessage({
                id: 'tag.create_error',
              }),
            });
          }
        });
    }
  };

  const handleCancelCreateModal = () => {
    tagForm.resetFields();
    form.resetFields(['tag']);
    setShowCreateModal(false);
  };

  const handleDeleteTag = (tag) => {
    AntdModal.confirm({
      title: intl.formatMessage({ id: 'tag.delete.confirm' }),
      content: intl.formatMessage(
        { id: 'tag.delete.content' },
        { name: tag.name },
      ),
      okText: intl.formatMessage({ id: 'delete' }),
      okButtonProps: { danger: true },
      cancelText: intl.formatMessage({ id: 'cancel' }),
      onOk: async () => {
        try {
          await deleteTag(tag.id);
          const tagsResponse = await getTagsByProject(project.id_project);
          setTags(tagsResponse.data.data);
          const currentTag = form.getFieldValue('tag');
          if (currentTag === tag.id) {
            form.resetFields(['tag']);
          }
          pushNotification({
            type: 'success',
            message: intl.formatMessage({
              id: 'tag.delete.success',
            }),
          });
        } catch (error) {
          pushNotification({
            type: 'error',
            message: intl.formatMessage({
              id: 'tag.delete.error',
            }),
          });
        }
      },
    });
  };

  return (
    <>
      <AntdFormItem
        label={intl.formatMessage({ id: 'tag' })}
        name="tag"
        rules={
          required
            ? [
                {
                  required: true,
                  message: intl.formatMessage({ id: 'required' }),
                },
              ]
            : undefined
        }
      >
        <AntdSelect
          disabled={disabled}
          placeholder={intl.formatMessage({ id: 'tag.select.placeholder' })}
          loading={fetchingTags}
          optionLabelProp="label"
          onChange={(value) => {
            if (value === 'newTag') {
              handleCreateTag();
            }
          }}
          optionRender={(option) => {
            if (option.value === 'newTag') {
              return option.label;
            }
            const tag = tags.find((t) => t.id === option.value);
            if (!tag) return option.label;
            return (
              <Flex justify="space-between" align="center">
                <span>
                  {tag.name} ---{' '}
                  <span style={{ color: '#999999' }}>
                    {intl.formatMessage(
                      { id: 'tag.batches.count' },
                      { count: tag.tag_batch_data?.length || 0 },
                    )}
                  </span>
                </span>
                <Tooltip
                  title={
                    tag.tag_batch_data?.length > 0
                      ? intl.formatMessage(
                          { id: 'tag.delete.disabled_has_batches' },
                          {
                            defaultMessage:
                              'No se puede eliminar porque hay lotes vinculados a esta etiqueta',
                          },
                        )
                      : ''
                  }
                >
                  <Button
                    disabled={tag.tag_batch_data?.length > 0}
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteTag(tag);
                    }}
                  />
                </Tooltip>
              </Flex>
            );
          }}
          options={[
            {
              value: 'newTag',
              label: (
                <Flex gap={5} align="center">
                  <PlusOutlined />{' '}
                  <strong>
                    {intl.formatMessage({
                      id: 'tag.select.newTag',
                    })}
                  </strong>
                </Flex>
              ),
            },
            ...tags.map((tag) => ({
              value: tag.id,
              label: `${tag.name} --- ${intl.formatMessage(
                { id: 'tag.batches.count' },
                { count: tag.tag_batch_data?.length || 0 },
              )}`,
            })),
          ]}
        />
      </AntdFormItem>

      <Modal
        open={showCreateModal}
        onCancel={handleCancelCreateModal}
        width="35vw"
        closable
        title={intl.formatMessage({ id: 'tag.create_new' }).toUpperCase()}
      >
        <div style={{ padding: '10px 20px' }}>
          <Form
            form={tagForm}
            layout="vertical"
            onFinish={handleSubmitTag}
            onFinishFailed={() => {
              pushNotification({
                type: 'error',
                message: intl.formatMessage({
                  id: 'validations.form_has_errors',
                }),
              });
            }}
          >
            <AntdFormItem
              name="tagName"
              label={intl.formatMessage({ id: 'tag.name' })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({ id: 'validations.required' }),
                },
                {
                  validator: (_, value) => {
                    if (value && value.trim() === '') {
                      return Promise.reject(
                        new Error(
                          intl.formatMessage({
                            id: 'validations.no.whitespace.only',
                          }),
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
                {
                  max: 8,
                  message: intl.formatMessage(
                    { id: 'validations.maxLength' },
                    { max: 8 },
                  ),
                },
                {
                  pattern: /^[a-zA-Z0-9 -]*$/,
                  message: intl.formatMessage(
                    { id: 'tag.invalid_format' },
                    {
                      defaultMessage:
                        'Solo se permiten letras, números, espacios y guiones',
                    },
                  ),
                },
              ]}
            >
              <AntdInput
                size="large"
                placeholder={intl.formatMessage({ id: 'tag.name' })}
              />
            </AntdFormItem>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '30px',
              }}
            >
              <AccionaButton
                type="default"
                onClick={handleCancelCreateModal}
                msg={intl.formatMessage({ id: 'cancel' })}
              />
              <AccionaButton
                onClick={() => tagForm.submit()}
                msg={intl.formatMessage({ id: 'create' })}
              />
            </div>
          </Form>
        </div>
      </Modal>
    </>
  );
};

TagSelector.propTypes = {
  form: PropTypes.object.isRequired,
  project: PropTypes.shape({
    version: PropTypes.number,
    id_project: PropTypes.number,
  }),
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  tags: PropTypes.array,
  fetchingTags: PropTypes.bool,
  setTags: PropTypes.func,
};

TagSelector.defaultProps = {
  project: undefined,
  disabled: false,
  required: false,
  tags: [],
  fetchingTags: false,
  setTags: () => {},
};

export default TagSelector;
